import React from "react";
import { SafeAreaView, View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { router } from "expo-router";
import { AppText } from "@/components/AppText";
import { useBillUpload } from "@/hooks/useBillUpload";
import { AnimatedStatusText } from "@/components/bills/AnimatedStatusText";
import { AnimatedProgressBar } from "@/components/bills/AnimatedProgressBar";
import Icons from "@/assets/icons";

const UploadBillScreen: React.FC = () => {
  const insets = useSafeAreaInsets();

  const {
    isUploading,
    uploadProgress,
    uploadStatus,
    selectAndUploadBill,
    uploadBill: _uploadBill,
  } = useBillUpload(
    (uploadedBill) => {
      // On successful upload, navigate to bill detail screen with bill data
      router.push({
        pathname: "/bills/detail",
        params: {
          billId: `${uploadedBill.accountNumber}#${uploadedBill.billingDate}`,
        },
      });
    },
    () => {}
  );

  const handleUploadFile = async () => {
    await selectAndUploadBill();
  };

  const cancelSectionStyle = [
    styles.cancelSection,
    { paddingBottom: insets.bottom === 0 ? 16 : insets.bottom },
  ];

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        {/* Title Section */}
        <View style={styles.titleSection}>
          <AppText style={styles.title}>Upload Your Bill</AppText>
          <AppText style={styles.subtitle}>
            Upload or take a photo of your bill so we can check for hidden
            savings.
          </AppText>
        </View>

        {/* Upload Options or Uploading State */}
        {isUploading ? (
          <View style={styles.uploadingContainer}>
            {/* Document Illustration */}
            <View style={styles.documentContainer}>
              <View style={styles.documentPaper}>
                <View style={styles.documentLines}>
                  <View style={styles.documentLine} />
                  <View style={styles.documentLine} />
                  <View style={styles.documentLine} />
                  <View style={styles.documentLine} />
                  <View style={styles.documentLineShort} />
                </View>
              </View>
              <View style={styles.documentCorner} />
            </View>

            {/* Uploading Text */}
            <AnimatedStatusText
              status={uploadStatus}
              style={styles.uploadingText}
            />

            {/* Progress Bar */}
            <AnimatedProgressBar
              progress={uploadProgress}
              containerStyle={styles.progressBarContainer}
              height={6}
              backgroundColor="#FFFFFF"
              progressColor="#0F9129"
            />
          </View>
        ) : (
          <View style={styles.optionsContainer}>
            {/* Upload Bill Option */}
            <TouchableOpacity
              style={styles.optionCard}
              onPress={handleUploadFile}
              activeOpacity={0.8}
            >
              <View style={styles.optionContent}>
                <View style={styles.optionRow}>
                  <View style={styles.iconContainer}>
                    <Icons.Upload style={styles.icon} />
                  </View>
                  <View style={styles.textContainer}>
                    <AppText style={styles.optionTitle}>
                      Upload your bill
                    </AppText>
                    <AppText style={styles.optionDescription}>
                      Select a file or photo from your library
                    </AppText>
                  </View>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Cancel Button at Bottom */}
      <View style={cancelSectionStyle}>
        <TouchableOpacity onPress={() => router.back()} activeOpacity={0.8}>
          <AppText style={styles.cancelText}>Cancel</AppText>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

export default UploadBillScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 24,
    gap: 32,
  },
  titleSection: {
    gap: 8,
  },
  title: {
    fontFamily: FontFamilies.InterTight.Medium,
    fontSize: 24,
    lineHeight: 32,
    color: "#161D14",
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    letterSpacing: -0.3,
    color: "rgba(22, 29, 20, 0.7)",
  },
  optionsContainer: {
    gap: 16,
  },
  optionCard: {
    backgroundColor: "#F4F5F6",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#A3A3A3",
    borderStyle: "dashed",
    padding: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  optionContent: {
    width: "100%",
    gap: 8,
  },
  optionRow: {
    flexDirection: "row",
    gap: 16,
    alignItems: "center",
  },
  iconContainer: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  icon: {
    width: 24,
    height: 24,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  optionTitle: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 17,
    lineHeight: 25,
    color: "#151515",
  },
  optionDescription: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    letterSpacing: -0.26,
    color: "#151515",
  },
  cancelSection: {
    alignItems: "center",
    paddingTop: 24,
  },
  cancelText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    color: "#414141",
  },
  uploadingContainer: {
    backgroundColor: "#F4F5F6",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#0F9129",
    borderStyle: "dashed",
    padding: 80,
    alignItems: "center",
    gap: 24,
  },
  documentContainer: {
    width: 93,
    height: 99,
    position: "relative",
  },
  documentPaper: {
    width: 85.54,
    height: 99,
    backgroundColor: "#FFFFFF",
    borderRadius: 4,
    position: "absolute",
    left: 0,
    top: 0,
  },
  documentLines: {
    position: "absolute",
    left: 14,
    top: 24.81,
    gap: 10,
  },
  documentLine: {
    width: 44,
    height: 3,
    backgroundColor: "#E3E9ED",
    marginBottom: 10,
  },
  documentLineShort: {
    width: 29,
    height: 3,
    backgroundColor: "#E3E9ED",
  },
  documentCorner: {
    width: 20,
    height: 40,
    backgroundColor: "#CED0D3",
    position: "absolute",
    right: 0,
    top: 58.81,
    borderTopLeftRadius: 4,
  },
  uploadingText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 17,
    lineHeight: 25,
    color: "#151515",
    textAlign: "center",
  },
  progressBarContainer: {
    width: 240,
    height: 6,
    backgroundColor: "#FFFFFF",
    borderRadius: 100,
  },
  progressBar: {
    height: 6,
    backgroundColor: "#0F9129",
    borderRadius: 100,
  },
});
